import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {Notation} from 'biojs/BioConstants';
import {EMPTY_MOLFILE} from 'chemistry/ChemistryConstants';
import {
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import ChemicalStructureModalInput from 'chemistry/molecules/ChemicalStructureModalInput';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as FileConstants from 'folder-items/FileConstants';
import {
  ComplexPolymerConnection,
  ComplexPolymerType,
  ResolvedAttachmentInput,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {graphqlSchemaInterfaceToComplexPolymerType} from 'modbio/complex-polymers/ComplexPolymerSchemaInterfaceConstants';
import {
  ComplexPolymerCreateStep,
  CreateComplexPolymerModalContents,
} from 'modbio/complex-polymers/create/CreateComplexPolymerModal';
import {sanitizeResiduesByNotation, sequenceToNucleotideType} from 'molbio/OligoUtil';
import ResidueNotationDropdown from 'molbio/ResidueNotationDropdown';
import CreateBioentityTagInputs from 'registry/create-bioentity/CreateBioentityTagInputs';
import {CreateBioentityArgs} from 'registry/create-bioentity/CreateBioentityUtil';
import ProjectFolderField from 'registry/ProjectFolderField';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import {InputChangeEvent, Optional} from 'util/Types';

import {CreateAndRegisterStep} from './CreateAndRegisterBioentityModal';

interface NameFieldProps {
  onChange: (e: InputChangeEvent) => void;
  name: string;
  labelStyle: LabelStyle;
}

function NameField({name, onChange, labelStyle}: NameFieldProps): JSX.Element {
  return (
    <TextField
      label="Entity name"
      labelStyle={labelStyle}
      onChange={onChange}
      placeholder="Name"
      value={name}
      isRequired={true}
      dataTestAttrs={{component: 'CreateBioentityInputs', element: 'EntityNameTextField'}}
    />
  );
}

interface ResiduesFieldProps {
  labelStyle: LabelStyle;
  onChange: (e: InputChangeEvent) => void;
  residues: string;
}

function ResiduesField({residues, onChange, labelStyle}: ResiduesFieldProps): JSX.Element {
  return (
    <TextField
      label="Bases"
      labelStyle={labelStyle}
      onChange={onChange}
      placeholder="Bases"
      value={residues}
      isRequired={false}
    />
  );
}

export interface CreateBioentityInputsProps {
  createBioentityArgs: CreateBioentityArgs;
  isOligoSchema: boolean;
  labelStyle: LabelStyle;
  onChange: (createBioentityArgs: Partial<CreateBioentityArgs>) => void;
  shouldShowNameField: boolean;
  showStudiesField: boolean;
  isProjectFolderRequired: boolean;
  createStep?: CreateAndRegisterStep | null;
  setCreateStep?: (createStep: CreateAndRegisterStep | null) => void;
}

export default class CreateBioentityInputs extends React.Component<CreateBioentityInputsProps> {
  _handleChange(update: Partial<CreateBioentityArgs>): void {
    this.props.onChange(update);
  }

  _handleChangeName = (e: InputChangeEvent): void => {
    this._handleChange({name: e.target.value});
  };

  _handleChangeProjectFolder = (e: InputChangeEvent): void => {
    // If e.target.value is falsey (e.g. empty string), fallback to null (see BNCH-10194)
    this._handleChange({folderId: e.target.value || null});
  };

  _handleChangeSchemaTags = (tags: Array<FileConstants.SchemaTag>): void => {
    this._handleChange({tags});
  };

  _handleChangeResidues = (e: InputChangeEvent): void => {
    this._handleChange({
      residues: sanitizeResiduesByNotation(
        e.target.value,
        this.props.createBioentityArgs.notation,
        sequenceToNucleotideType(this.props.createBioentityArgs.tagSchema?.sequenceType)
      ),
    });
  };

  _handleSelectNotation = (newNotation: Notation): void => {
    const residues = this.props.createBioentityArgs.residues || '';
    this._handleChange({
      residues: sanitizeResiduesByNotation(
        residues,
        newNotation,
        sequenceToNucleotideType(this.props.createBioentityArgs.tagSchema?.sequenceType)
      ),
      notation: newNotation,
    });
  };

  _handleSelectCustomSyntaxId = (customSyntaxId: string): void => {
    this._handleChange({customSyntaxId});
  };

  _handleChangeSketcherValue = (sketcherValue: ChemicalStructureDrawerValue): void => {
    this._handleChange({
      chemicalSketcherValue: sketcherValue,
    });
  };

  _handleChangeSmiles = (smiles: string): void => {
    this._handleChange({
      smiles,
    });
  };

  _handleChangeStudySearchItems = (studySearchItems: Array<StudySearchItem>): void => {
    this._handleChange({studySearchItems});
  };

  _handleChangeComplexPolymerTags = (complexPolymerTags: Array<FileConstants.SchemaTag>): void => {
    this._handleChange({complexPolymerTags});
  };

  _handleChangeComplexPolymerConnections = (
    complexPolymerConnections: Array<ComplexPolymerConnection>
  ): void => {
    this._handleChange({complexPolymerConnections});
  };

  _handleChangeComplexPolymerResolvedAttachments = (
    complexPolymerResolvedAttachments: Array<ResolvedAttachmentInput>
  ): void => {
    this._handleChange({complexPolymerResolvedAttachments});
  };

  _handleChangeOligoDuplexOffset = (oligoDuplexOffset: Optional<number>): void => {
    this._handleChange({oligoDuplexOffset});
  };

  render(): JSX.Element {
    const {
      createBioentityArgs: {
        name,
        folderId,
        notation,
        customSyntaxId,
        tagSchema,
        tags,
        studySearchItems,
        complexPolymerTags,
        complexPolymerConnections,
        complexPolymerResolvedAttachments,
        oligoDuplexOffset,
        complexPolymerSchemaConfig,
      },
      isOligoSchema,
      isProjectFolderRequired,
      createStep,
      setCreateStep,
    } = this.props;
    const residues = this.props.createBioentityArgs.residues ? this.props.createBioentityArgs.residues : '';
    const shouldShowComplexPolymerMenus =
      BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES && complexPolymerSchemaConfig != null;
    assert(
      !shouldShowComplexPolymerMenus || setCreateStep != null,
      'Expected setCreateStep to be defined for complex polymers.'
    );

    const complexPolymerModalContents = shouldShowComplexPolymerMenus ? (
      <CreateComplexPolymerModalContents
        complexPolymerType={graphqlSchemaInterfaceToComplexPolymerType(complexPolymerSchemaConfig)}
        complexPolymerSchemaConfig={complexPolymerSchemaConfig}
        complexPolymerComponents={complexPolymerTags ?? []}
        complexPolymerConnections={complexPolymerConnections ?? []}
        oligoDuplexOffset={
          graphqlSchemaInterfaceToComplexPolymerType(complexPolymerSchemaConfig) ===
          ComplexPolymerType.OLIGO_DUPLEX
            ? (oligoDuplexOffset ?? 0)
            : null
        }
        step={createStep ?? ComplexPolymerCreateStep.SELECT_ENTITIES}
        setStep={setCreateStep!}
        setComplexPolymerConnections={this._handleChangeComplexPolymerConnections}
        setComplexPolymerComponents={this._handleChangeComplexPolymerTags}
        setOffset={this._handleChangeOligoDuplexOffset}
        resolvedAttachmentInputs={complexPolymerResolvedAttachments ?? []}
        setResolvedAttachmentInputs={this._handleChangeComplexPolymerResolvedAttachments}
      />
    ) : null;

    // The complex polymer attachments pane should take up the whole modal when we're on that step.
    if (
      shouldShowComplexPolymerMenus &&
      createStep === ComplexPolymerCreateStep.CONFIGURE_ATTACHMENTS &&
      complexPolymerModalContents != null
    ) {
      return complexPolymerModalContents;
    }

    const shouldShowStudiesField = BenchlingConfig.STUDIES_ENABLE && this.props.showStudiesField;

    return (
      <div className={css(styles.wrapper)}>
        {this.props.shouldShowNameField && (
          <FormGroup>
            <NameField name={name} onChange={this._handleChangeName} labelStyle={this.props.labelStyle} />
          </FormGroup>
        )}
        <FormGroup>
          <Row>
            <Col sm={shouldShowStudiesField ? 6 : 12}>
              <ProjectFolderField
                folderId={folderId}
                labelStyle={this.props.labelStyle}
                onChange={this._handleChangeProjectFolder}
                isRequired={isProjectFolderRequired}
              />
            </Col>
            {shouldShowStudiesField && (
              <Col sm={6}>
                <FieldLabel label="Studies" />
                <StudySearchInput
                  selectedItems={studySearchItems ?? []}
                  onChange={this._handleChangeStudySearchItems}
                  shouldAllowMultiple={true}
                />
              </Col>
            )}
          </Row>
        </FormGroup>

        <FormGroup>
          <CreateBioentityTagInputs
            labelStyle={this.props.labelStyle}
            onChange={this._handleChangeSchemaTags}
            tagSchemaId={tagSchema ? tagSchema.id : null}
            tags={tags}
          />
        </FormGroup>

        {BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES && isOligoSchema && (
          <ResidueNotationDropdown
            notation={notation}
            onSelectNotation={this._handleSelectNotation}
            selectedCustomSyntaxId={customSyntaxId}
            onSelectCustomSyntaxId={this._handleSelectCustomSyntaxId}
            componentName="CreateBioentityInputs"
          />
        )}

        {isOligoSchema && (
          <FormGroup>
            <ResiduesField
              labelStyle={this.props.labelStyle}
              onChange={this._handleChangeResidues}
              residues={residues}
            />
          </FormGroup>
        )}

        {BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE &&
          tagSchema?.folderItemType === ModelTypeConstants.MOLECULE && (
            <ChemicalStructureModalInput
              initialSketcherValue={EMPTY_MOLFILE}
              onSketcherValueChange={this._handleChangeSketcherValue}
              mode={ChemicalStructureDrawerMode.BASIC_SEARCH}
              inputFormat={null}
              outputFormat={MarvinChemicalFormat.MOLV3}
            />
          )}

        {shouldShowComplexPolymerMenus && complexPolymerModalContents!}
      </div>
    );
  }
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
});
